import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { catalogoService, usuarioService } from 'services/usuarioService';
import { reporteService, reporteCobrosService } from 'services/reporteService';
import {
  ReporteNotasSeccion, ReporteAsistenciaEstudiante, ReporteAsistenciaSeccion,
  ReporteMorosidad, ReporteIngresos, ReporteDescuentos,
  ReporteMatriculas, ReporteCargaDocente, ReporteCobros,
} from 'types/reporte.types';
import { UsuarioListItem } from 'types/usuario.types';
import { exportarExcel, exportarPDF } from 'utils/reporteExport';
import { documentoService } from 'services/documentoService';
import { htmlRod } from 'utils/documentoPdf';
import { I, PageHead, Card } from 'components/ui';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// ReportesPage
// ============================================================

type TabKey = 'notas' | 'asist-est' | 'asist-sec' | 'morosidad' | 'ingresos' | 'descuentos' | 'matriculas' | 'carga' | 'rod' | 'cobros';

interface TabDef { key: TabKey; label: string; roles: string[]; }

const TABS: TabDef[] = [
  { key: 'notas',      label: 'Notas por Sección',      roles: ['ADMIN','DIRECTOR','DOCENTE'] },
  { key: 'asist-est',  label: 'Asistencia Estudiante',  roles: ['ADMIN','DIRECTOR','DOCENTE'] },
  { key: 'asist-sec',  label: 'Asistencia por Sección', roles: ['ADMIN','DIRECTOR','DOCENTE'] },
  { key: 'morosidad',  label: 'Morosidad',              roles: ['ADMIN','DIRECTOR','SECRETARIA'] },
  { key: 'ingresos',   label: 'Ingresos',               roles: ['ADMIN','DIRECTOR','SECRETARIA'] },
  { key: 'descuentos', label: 'Descuentos',             roles: ['ADMIN','DIRECTOR','SECRETARIA'] },
  { key: 'matriculas', label: 'Matrículas',             roles: ['ADMIN','DIRECTOR','SECRETARIA'] },
  { key: 'carga',      label: 'Carga Docente',          roles: ['ADMIN','DIRECTOR'] },
  { key: 'rod',        label: 'ROD',                    roles: ['ADMIN','DIRECTOR'] },
  { key: 'cobros',     label: 'Cobros Adicionales',     roles: ['ADMIN','DIRECTOR','SECRETARIA'] },
];

const NIVEL_COLOR: Record<string, string> = {
  INICIAL:    'bg-amber-100 text-amber-700',
  PRIMARIA:   'bg-blue-100 text-blue-700',
  SECUNDARIA: 'bg-purple-100 text-purple-700',
};
const MESES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const sol  = (n: number) => new Intl.NumberFormat('es-PE',{ style:'currency', currency:'PEN' }).format(n);
const pct  = (n: number) => `${n.toFixed(1)}%`;
const nota = (n: number | null) => n === null ? '—' : n.toFixed(2);

const Spin = () => (
  <div className="flex justify-center items-center py-20">
    <svg className="animate-spin w-8 h-8 text-blue-500" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
    </svg>
  </div>
);

const Empty = ({ msg = 'Sin datos' }: { msg?: string }) => (
  <div className="text-center py-16 text-slate-400 text-sm">{msg}</div>
);

const ErrBox = ({ msg }: { msg: string }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{msg}</div>
);

const KpiCard = ({ label, value, sub, color = 'bg-blue-100 text-blue-700' }: {
  label: string; value: string; sub?: string; color?: string;
}) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-5 py-4">
    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-slate-800 mt-1 tabular-nums">{value}</p>
    {sub && <p className={`text-xs font-semibold mt-0.5 px-2 py-0.5 rounded-full w-fit ${color}`}>{sub}</p>}
  </div>
);

const BtnDescarga = ({ onExcel, onPDF }: { onExcel: () => void; onPDF: () => void }) => (
  <div className="flex items-center gap-2">
    <button onClick={onExcel} title="Descargar Excel"
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700
                 text-white text-xs font-bold transition-colors shadow-sm">
      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
        <path fillRule="evenodd" d="M3 17a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm3.293-7.707a1 1 0 0 1 1.414 0L9 10.586V3a1 1 0 1 1 2 0v7.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 0-1.414Z" clipRule="evenodd"/>
      </svg>
      Excel
    </button>
    <button onClick={onPDF} title="Descargar PDF"
      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700
                 text-white text-xs font-bold transition-colors shadow-sm">
      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
        <path fillRule="evenodd" d="M3 17a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1Zm3.293-7.707a1 1 0 0 1 1.414 0L9 10.586V3a1 1 0 1 1 2 0v7.586l1.293-1.293a1 1 0 1 1 1.414 1.414l-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 0-1.414Z" clipRule="evenodd"/>
      </svg>
      PDF
    </button>
  </div>
);

// ── Filtro de año + grado + sección ────────────────────────────
const useSeccionFiltro = (años: AñoEscolar[], grados: Grado[], secciones: Seccion[]) => {
  const [anioId,    setAnioId]    = useState('');
  const [gradoId,   setGradoId]   = useState('');
  const [seccionId, setSeccionId] = useState('');

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const gradosFiltrados    = grados;
  const seccionesFiltradas = gradoId
    ? secciones.filter(s => s.gradoId === Number(gradoId))
    : secciones;

  const ready = !!(anioId && gradoId && seccionId);

  return { anioId, setAnioId, gradoId, setGradoId, seccionId, setSeccionId,
           gradosFiltrados, seccionesFiltradas, ready };
};

const SelectField = ({ label, value, onChange, children, disabled }: {
  label: string; value: string; onChange: (v: string) => void;
  children: React.ReactNode; disabled?: boolean;
}) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700
                 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
      {children}
    </select>
  </div>
);

const BtnGenerar = ({ onClick, loading, disabled }: { onClick: () => void; loading: boolean; disabled?: boolean }) => (
  <button onClick={onClick} disabled={loading || disabled}
    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
               text-white text-sm font-bold transition-colors shadow-sm self-end
               disabled:opacity-60 disabled:cursor-not-allowed">
    {loading
      ? <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
        </svg>
      : <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
          <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41Z" clipRule="evenodd"/>
        </svg>}
    Generar reporte
  </button>
);

// ── Tabla base ──────────────────────────────────────────────────
const Tabla = ({ heads, children }: { heads: string[]; children: React.ReactNode }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50">
            {heads.map(h => (
              <th key={h} className="text-left px-4 py-3 text-xs font-bold text-slate-400
                                     uppercase tracking-wide whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">{children}</tbody>
      </table>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════
// TAB 1 – Notas por Sección
// ══════════════════════════════════════════════════════════════
const TabNotasSeccion: React.FC<{ años: AñoEscolar[]; grados: Grado[]; secciones: Seccion[] }> = ({ años, grados, secciones }) => {
  const f = useSeccionFiltro(años, grados, secciones);
  const [nivel, setNivel] = useState('');
  const [data,    setData]    = useState<ReporteNotasSeccion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const niveles = Array.from(new Set(grados.map(g => g.nivel))).sort();
  const gradosPorNivel = nivel ? grados.filter(g => g.nivel === nivel) : grados;

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      setData(await reporteService.notasSeccion({
        anioEscolarId: Number(f.anioId),
        nivel,
        gradoId:       Number(f.gradoId),
        seccionId:     Number(f.seccionId),
      }));
    } catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const usaLetra = nivel === 'INICIAL' || nivel === 'PRIMARIA';
    const headers = usaLetra
      ? ['Estudiante', 'DNI', ...data.cursos]
      : ['Estudiante', 'DNI', ...data.cursos, 'Promedio'];
    const filas: (string | number | null)[][] = [
      [`Notas — ${data.gradoNivel} · ${data.gradoNombre} Sección ${data.seccionNombre} · ${data.anio}`],
      [`Nivel: ${data.gradoNivel}  |  Grado: ${data.gradoNombre}  |  Sección: ${data.seccionNombre}  |  Año: ${data.anio}`],
      [],
      headers,
      ...data.estudiantes.map(est => [
        `${est.apellidos}, ${est.nombres}`,
        est.dni,
        ...data.cursos.map(c => {
          const cur = est.cursos.find(x => x.cursoNombre === c);
          return usaLetra ? (cur?.notaLetra ?? null) : (cur?.notaFinal ?? null);
        }),
        ...(usaLetra ? [] : [est.promedioGeneral]),
      ]),
    ];
    exportarExcel(`Notas_${data.gradoNivel}_${data.gradoNombre}_${data.seccionNombre}_${data.anio}`, [{ nombre: 'Notas', filas }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const usaLetra = nivel === 'INICIAL' || nivel === 'PRIMARIA';
    const letraColor = (l: string | null | undefined) => {
      if (!l) return '#94a3b8';
      if (l === 'AD' || l === 'A') return '#16a34a';
      if (l === 'B') return '#d97706';
      return '#dc2626'; // C
    };
    const thCursos = data.cursos.map(c => `<th>${c}</th>`).join('');
    const rows = data.estudiantes.map(est => {
      const tdCursos = data.cursos.map(c => {
        const curso = est.cursos.find(x => x.cursoNombre === c);
        if (usaLetra) {
          const l = curso?.notaLetra ?? '—';
          return `<td style="text-align:center;color:${letraColor(curso?.notaLetra)};font-weight:700;">${l}</td>`;
        }
        const n = curso ? nota(curso.notaFinal) : '—';
        const color = curso?.aprobado === true ? '#16a34a' : curso?.aprobado === false ? '#dc2626' : '#94a3b8';
        return `<td style="text-align:center;color:${color};font-weight:700;">${n}</td>`;
      }).join('');
      const tdPromedio = usaLetra ? '' : (() => {
        const promColor = est.promedioGeneral !== null ? (est.promedioGeneral >= 11 ? '#16a34a' : '#dc2626') : '#94a3b8';
        return `<td style="text-align:center;color:${promColor};font-weight:700;">${nota(est.promedioGeneral)}</td>`;
      })();
      return `<tr><td>${est.apellidos}, ${est.nombres}<br/><span style="color:#94a3b8;font-size:9px;">${est.dni}</span></td>${tdCursos}${tdPromedio}</tr>`;
    }).join('');
    const thPromedio = usaLetra ? '' : '<th>Promedio</th>';
    exportarPDF(
      `Notas — ${data.gradoNivel} · ${data.gradoNombre} Sección ${data.seccionNombre} · ${data.anio}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Nivel</div><div class="kpi-val" style="font-size:13px;">${data.gradoNivel}</div></div>
        <div class="kpi"><div class="kpi-lbl">Grado</div><div class="kpi-val" style="font-size:13px;">${data.gradoNombre}</div></div>
        <div class="kpi"><div class="kpi-lbl">Sección</div><div class="kpi-val" style="font-size:13px;">${data.seccionNombre}</div></div>
        <div class="kpi"><div class="kpi-lbl">Año</div><div class="kpi-val" style="font-size:13px;">${data.anio}</div></div>
        <div class="kpi"><div class="kpi-lbl">Estudiantes</div><div class="kpi-val" style="font-size:13px;">${data.totalEstudiantes}</div></div>
      </div>
      <table><thead><tr><th>Estudiante</th>${thCursos}${thPromedio}</tr></thead><tbody>${rows}</tbody></table>`,
    );
  };

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 items-end">
          <SelectField label="Año escolar" value={f.anioId} onChange={f.setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <SelectField label="Nivel" value={nivel} onChange={v => { setNivel(v); f.setGradoId(''); f.setSeccionId(''); }}>
            <option value="">Todos</option>
            {niveles.map(n => <option key={n} value={n}>{n}</option>)}
          </SelectField>
          <SelectField label="Grado" value={f.gradoId} onChange={v => { f.setGradoId(v); f.setSeccionId(''); }} disabled={!nivel}>
            <option value="">Seleccionar…</option>
            {gradosPorNivel.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>)}
          </SelectField>
          <SelectField label="Sección" value={f.seccionId} onChange={f.setSeccionId} disabled={!f.gradoId}>
            <option value="">Seleccionar…</option>
            {f.seccionesFiltradas.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!f.ready}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          {/* Encabezado */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${NIVEL_COLOR[data.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
              {data.gradoNivel}
            </span>
            <h2 className="font-bold text-slate-800">
              {data.gradoNombre} — Sección {data.seccionNombre} · {data.anio}
            </h2>
            <span className="text-xs text-slate-400">{data.totalEstudiantes} estudiantes</span>
            <div className="ml-auto"><BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/></div>
          </div>

          {/* Tabla */}
          {(() => {
            const usaLetra = nivel === 'INICIAL' || nivel === 'PRIMARIA';
            const letraCls = (l: string | null) => {
              if (!l) return 'text-slate-400';
              if (l === 'AD' || l === 'A') return 'text-green-600';
              if (l === 'B') return 'text-amber-600';
              return 'text-red-600'; // C
            };
            return (
              <>
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50">
                          <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase sticky left-0 bg-slate-50">Estudiante</th>
                          {data.cursos.map(c => (
                            <th key={c} className="text-center px-3 py-3 text-xs font-bold text-slate-400 uppercase whitespace-nowrap">{c}</th>
                          ))}
                          {!usaLetra && (
                            <th className="text-center px-3 py-3 text-xs font-bold text-slate-400 uppercase">Promedio</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {data.estudiantes.map(est => (
                          <tr key={est.estudianteId} className="hover:bg-slate-50">
                            <td className="px-4 py-3 sticky left-0 bg-white hover:bg-slate-50">
                              <p className="font-semibold text-slate-800">{est.apellidos}, {est.nombres}</p>
                              <p className="text-xs text-slate-400">{est.dni}</p>
                            </td>
                            {data.cursos.map(cursoNombre => {
                              const c = est.cursos.find(x => x.cursoNombre === cursoNombre);
                              return (
                                <td key={cursoNombre} className="px-3 py-3 text-center">
                                  {c ? (
                                    usaLetra ? (
                                      <span className={`font-bold text-sm ${letraCls(c.notaLetra)}`}>
                                        {c.notaLetra ?? '—'}
                                      </span>
                                    ) : (
                                      <span className={`font-bold tabular-nums text-sm
                                        ${c.aprobado === true ? 'text-green-600' : c.aprobado === false ? 'text-red-600' : 'text-slate-400'}`}>
                                        {nota(c.notaFinal)}
                                      </span>
                                    )
                                  ) : <span className="text-slate-300">—</span>}
                                </td>
                              );
                            })}
                            {!usaLetra && (
                              <td className="px-3 py-3 text-center">
                                <span className={`font-bold tabular-nums
                                  ${est.promedioGeneral !== null
                                    ? est.promedioGeneral >= 11 ? 'text-green-600' : 'text-red-600'
                                    : 'text-slate-400'}`}>
                                  {nota(est.promedioGeneral)}
                                </span>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-xs text-slate-400 flex gap-4 flex-wrap">
                  {usaLetra ? (
                    <>
                      <span><span className="text-green-600 font-bold">AD</span> = Logro destacado</span>
                      <span><span className="text-green-600 font-bold">A</span> = Logro esperado</span>
                      <span><span className="text-amber-600 font-bold">B</span> = En proceso</span>
                      <span><span className="text-red-600 font-bold">C</span> = En inicio</span>
                    </>
                  ) : (
                    <>
                      <span><span className="text-green-600 font-bold">Verde</span> = aprobado (≥11)</span>
                      <span><span className="text-red-600 font-bold">Rojo</span> = desaprobado (&lt;11)</span>
                    </>
                  )}
                </p>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 2 – Asistencia por Estudiante
// ══════════════════════════════════════════════════════════════
const TabAsistenciaEstudiante: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,    setAnioId]    = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [busqueda,  setBusqueda]  = useState('');
  const [resultados,setResultados]= useState<UsuarioListItem[]>([]);
  const [buscando,  setBuscando]  = useState(false);
  const [estId,     setEstId]     = useState<number | null>(null);
  const [estNombre, setEstNombre] = useState('');
  const [data,      setData]      = useState<ReporteAsistenciaEstudiante | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [cursoAbierto, setCursoAbierto] = useState<number | null>(null);

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const buscar = async () => {
    if (!busqueda.trim()) return;
    setBuscando(true);
    try { setResultados(await usuarioService.listarEstudiantes({ search: busqueda })); }
    catch {} finally { setBuscando(false); }
  };

  const seleccionar = (u: UsuarioListItem) => {
    setEstId(u.idUsuario);
    setEstNombre(`${u.nombres} ${u.apellidos}`);
    setBusqueda(`${u.nombres} ${u.apellidos}`);
    setResultados([]);
  };

  const generar = async () => {
    if (!estId || !anioId) return;
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.asistenciaEstudiante(estId, Number(anioId))); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const nombre = `${data.apellidos}_${data.nombres}`;
    const hojas = [
      {
        nombre: 'Resumen',
        filas: [
          [`Asistencia — ${nombre} · ${data.anio}`],
          [`Nivel: ${data.gradoNivel}  Grado: ${data.gradoNombre}  Sección: ${data.seccionNombre}`],
          [],
          ['Curso', 'Docente', 'Total sesiones', 'Presentes', 'Tardanzas', 'Justificados', 'Ausentes', '% Asistencia'],
          ...data.cursos.map(c => [
            c.cursoNombre,
            `${c.docenteApellidos}, ${c.docenteNombres}`,
            c.totalSesiones, c.presentes, c.tardanzas, c.justificados, c.ausentes,
            `${c.porcentajeAsistencia.toFixed(1)}%`,
          ]),
        ] as (string | number | null)[][],
      },
      ...data.cursos.map(c => ({
        nombre: c.cursoNombre.slice(0, 31),
        filas: [
          [c.cursoNombre],
          [`Docente: ${c.docenteApellidos}, ${c.docenteNombres}`],
          [],
          ['Fecha', 'Estado', 'Motivo'],
          ...c.detalle.map(d => [d.fecha, d.estado, d.motivo ?? '']),
        ] as (string | number | null)[][],
      })),
    ];
    exportarExcel(`Asistencia_${nombre}_${data.anio}`, hojas);
  };

  const handlePDF = () => {
    if (!data) return;
    const resumenRows = data.cursos.map(c =>
      `<tr><td>${c.cursoNombre}</td><td>${c.docenteApellidos}, ${c.docenteNombres}</td>
       <td style="text-align:center;">${c.totalSesiones}</td>
       <td style="text-align:center;color:#16a34a;font-weight:700;">${c.presentes}</td>
       <td style="text-align:center;color:#d97706;font-weight:700;">${c.tardanzas}</td>
       <td style="text-align:center;color:#2563eb;font-weight:700;">${c.justificados}</td>
       <td style="text-align:center;color:#dc2626;font-weight:700;">${c.ausentes}</td>
       <td style="text-align:center;font-weight:700;">${c.porcentajeAsistencia.toFixed(1)}%</td></tr>`
    ).join('');
    exportarPDF(
      `Asistencia — ${data.apellidos}, ${data.nombres} · ${data.anio}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Estudiante</div><div class="kpi-val" style="font-size:12px;">${data.apellidos}, ${data.nombres}</div></div>
        <div class="kpi"><div class="kpi-lbl">DNI</div><div class="kpi-val" style="font-size:12px;">${data.dni}</div></div>
        <div class="kpi"><div class="kpi-lbl">Nivel</div><div class="kpi-val" style="font-size:12px;">${data.gradoNivel}</div></div>
        <div class="kpi"><div class="kpi-lbl">Grado / Sección</div><div class="kpi-val" style="font-size:12px;">${data.gradoNombre} — ${data.seccionNombre}</div></div>
      </div>
      <table><thead><tr><th>Curso</th><th>Docente</th><th>Sesiones</th><th>Presentes</th><th>Tardanzas</th><th>Justif.</th><th>Ausentes</th><th>% Asist.</th></tr></thead>
      <tbody>${resumenRows}</tbody></table>`,
    );
  };

  const EST_COLOR: Record<string, string> = {
    PRESENTE:   'bg-green-100 text-green-700',
    AUSENTE:    'bg-red-100 text-red-700',
    TARDANZA:   'bg-amber-100 text-amber-700',
    JUSTIFICADO:'bg-blue-100 text-blue-700',
  };

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>

          {/* Buscador de estudiante */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Estudiante</label>
            <div className="flex gap-2">
              <input value={busqueda} onChange={e => { setBusqueda(e.target.value); setEstId(null); }}
                onKeyDown={e => e.key === 'Enter' && buscar()}
                placeholder="Buscar por nombre o DNI…"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm
                           outline-none focus:ring-2 focus:ring-blue-500"/>
              <button onClick={buscar} disabled={buscando || !busqueda.trim()}
                className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-sm
                           font-semibold text-slate-600 disabled:opacity-50 transition-colors">
                {buscando
                  ? <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                    </svg>
                  : 'Buscar'}
              </button>
            </div>
            {resultados.length > 0 && (
              <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100 mt-1 z-10">
                {resultados.slice(0, 6).map(u => (
                  <button key={u.idUsuario} onClick={() => seleccionar(u)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left text-sm">
                    <span className="font-semibold text-slate-800">{u.apellidos}, {u.nombres}</span>
                    <span className="text-xs text-slate-400 ml-auto">{u.dni}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <BtnGenerar onClick={generar} loading={loading} disabled={!estId || !anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          {/* Encabezado */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-wrap gap-4 items-start">
            <div>
              <p className="text-xs text-slate-400">Estudiante</p>
              <p className="font-bold text-slate-800">{data.apellidos}, {data.nombres}</p>
              <p className="text-xs text-slate-400">DNI: {data.dni}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Grado / Sección</p>
              <p className="font-semibold text-slate-700">{data.gradoNombre} — Sección {data.seccionNombre}</p>
              <p className="text-xs text-slate-400">Año {data.anio}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Nivel</p>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${NIVEL_COLOR[data.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                {data.gradoNivel}
              </span>
            </div>
            <div className="ml-auto self-center"><BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/></div>
          </div>

          {/* Por curso */}
          {data.cursos.map(curso => (
            <div key={curso.asignacionId} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              {/* Cabecera curso */}
              <button className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                onClick={() => setCursoAbierto(prev => prev === curso.asignacionId ? null : curso.asignacionId)}>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{curso.cursoNombre}</p>
                  <p className="text-xs text-slate-400">Doc. {curso.docenteApellidos}, {curso.docenteNombres}</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-xs bg-green-100 text-green-700 font-bold px-2.5 py-1 rounded-full">
                    {pct(curso.porcentajeAsistencia)} asistencia
                  </span>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    {[
                      { label: 'Presentes', val: curso.presentes, c: 'text-green-600' },
                      { label: 'Tardanzas', val: curso.tardanzas, c: 'text-amber-600' },
                      { label: 'Justif.',   val: curso.justificados, c: 'text-blue-600' },
                      { label: 'Ausentes',  val: curso.ausentes, c: 'text-red-600' },
                    ].map(x => (
                      <div key={x.label}>
                        <p className={`text-base font-bold tabular-nums ${x.c}`}>{x.val}</p>
                        <p className="text-xs text-slate-400">{x.label}</p>
                      </div>
                    ))}
                  </div>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${cursoAbierto === curso.asignacionId ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
                  </svg>
                </div>
              </button>

              {/* Detalle colapsable */}
              {cursoAbierto === curso.asignacionId && (
                <div className="border-t border-slate-100">
                  <Tabla heads={['Fecha', 'Estado', 'Motivo']}>
                    {curso.detalle.map((d, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-slate-600 tabular-nums">{d.fecha}</td>
                        <td className="px-4 py-2.5">
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${EST_COLOR[d.estado]}`}>
                            {d.estado}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{d.motivo ?? '—'}</td>
                      </tr>
                    ))}
                  </Tabla>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 3 – Asistencia por Sección
// ══════════════════════════════════════════════════════════════
const TabAsistenciaSeccion: React.FC<{ años: AñoEscolar[]; grados: Grado[]; secciones: Seccion[] }> = ({ años, grados, secciones }) => {
  const f = useSeccionFiltro(años, grados, secciones);
  const [nivel, setNivel] = useState('');
  const [data,    setData]    = useState<ReporteAsistenciaSeccion | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [cursoIdx, setCursoIdx] = useState(0);

  const niveles        = Array.from(new Set(grados.map(g => g.nivel))).sort();
  const gradosPorNivel = nivel ? grados.filter(g => g.nivel === nivel) : grados;

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      setData(await reporteService.asistenciaSeccion({
        anioEscolarId: Number(f.anioId), nivel, gradoId: Number(f.gradoId), seccionId: Number(f.seccionId),
      }));
      setCursoIdx(0);
    } catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const hojas = data.cursos.map(c => ({
      nombre: c.cursoNombre.slice(0, 31),
      filas: [
        [`${c.cursoNombre} — ${data.gradoNombre} Sección ${data.seccionNombre} · ${data.anio}`],
        [`Docente: ${c.docenteApellidos}, ${c.docenteNombres}  |  Sesiones: ${c.totalSesiones}`],
        [],
        ['Estudiante', 'Presentes', 'Tardanzas', 'Justificados', 'Ausentes', '% Asistencia'],
        ...c.estudiantes.map(e => [
          `${e.apellidos}, ${e.nombres}`,
          e.presentes, e.tardanzas, e.justificados, e.ausentes,
          `${e.porcentajeAsistencia.toFixed(1)}%`,
        ]),
      ] as (string | number | null)[][],
    }));
    exportarExcel(`Asistencia_Seccion_${data.gradoNombre}_${data.seccionNombre}_${data.anio}`, hojas);
  };

  const handlePDF = () => {
    if (!data) return;
    const secciones = data.cursos.map(c => {
      const rows = c.estudiantes.map(e =>
        `<tr><td>${e.apellidos}, ${e.nombres}</td>
         <td style="text-align:center;color:#16a34a;font-weight:700;">${e.presentes}</td>
         <td style="text-align:center;color:#d97706;font-weight:700;">${e.tardanzas}</td>
         <td style="text-align:center;color:#2563eb;font-weight:700;">${e.justificados}</td>
         <td style="text-align:center;color:#dc2626;font-weight:700;">${e.ausentes}</td>
         <td style="text-align:center;font-weight:700;">${e.porcentajeAsistencia.toFixed(1)}%</td></tr>`
      ).join('');
      return `<div class="section-hdr">${c.cursoNombre} — Doc. ${c.docenteApellidos}, ${c.docenteNombres} · ${c.totalSesiones} sesiones</div>
        <table><thead><tr><th>Estudiante</th><th>Presentes</th><th>Tardanzas</th><th>Justif.</th><th>Ausentes</th><th>% Asist.</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
    }).join('');
    exportarPDF(
      `Asistencia Sección — ${data.gradoNivel} ${data.gradoNombre} ${data.seccionNombre} · ${data.anio}`,
      secciones,
    );
  };

  const curso = data?.cursos[cursoIdx];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 items-end">
          <SelectField label="Año escolar" value={f.anioId} onChange={f.setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <SelectField label="Nivel" value={nivel} onChange={v => { setNivel(v); f.setGradoId(''); f.setSeccionId(''); }}>
            <option value="">Todos</option>
            {niveles.map(n => <option key={n} value={n}>{n}</option>)}
          </SelectField>
          <SelectField label="Grado" value={f.gradoId} onChange={v => { f.setGradoId(v); f.setSeccionId(''); }} disabled={!nivel}>
            <option value="">Seleccionar…</option>
            {gradosPorNivel.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>)}
          </SelectField>
          <SelectField label="Sección" value={f.seccionId} onChange={f.setSeccionId} disabled={!f.gradoId}>
            <option value="">Seleccionar…</option>
            {f.seccionesFiltradas.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!f.ready}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${NIVEL_COLOR[data.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                {data.gradoNivel}
              </span>
              <h2 className="font-bold text-slate-800">
                {data.gradoNombre} — Sección {data.seccionNombre} · {data.anio}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">{data.totalEstudiantes} estudiantes</span>
              <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
            </div>
          </div>

          {/* Selector de curso */}
          {data.cursos.length > 1 && (
            <div className="flex gap-1 flex-wrap">
              {data.cursos.map((c, i) => (
                <button key={c.asignacionId} onClick={() => setCursoIdx(i)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors
                    ${i === cursoIdx ? 'bg-blue-700 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {c.cursoNombre}
                </button>
              ))}
            </div>
          )}

          {curso && (
            <>
              <div className="bg-white rounded-xl border border-slate-100 px-4 py-3 flex items-center gap-4 text-sm">
                <span className="font-bold text-slate-800">{curso.cursoNombre}</span>
                <span className="text-slate-400">Doc. {curso.docenteApellidos}, {curso.docenteNombres}</span>
                <span className="text-xs text-slate-400 ml-auto">{curso.totalSesiones} sesiones</span>
              </div>
              <Tabla heads={['Estudiante', 'Presentes', 'Tardanzas', 'Justif.', 'Ausentes', 'Asistencia']}>
                {curso.estudiantes.map(est => (
                  <tr key={est.estudianteId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      {est.apellidos}, {est.nombres}
                    </td>
                    <td className="px-4 py-3 text-center text-green-600 font-bold tabular-nums">{est.presentes}</td>
                    <td className="px-4 py-3 text-center text-amber-600 font-bold tabular-nums">{est.tardanzas}</td>
                    <td className="px-4 py-3 text-center text-blue-600 font-bold tabular-nums">{est.justificados}</td>
                    <td className="px-4 py-3 text-center text-red-600 font-bold tabular-nums">{est.ausentes}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                          <div className={`h-full rounded-full ${est.porcentajeAsistencia >= 75 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(est.porcentajeAsistencia, 100)}%` }}/>
                        </div>
                        <span className={`text-xs font-bold tabular-nums w-12 text-right
                          ${est.porcentajeAsistencia >= 75 ? 'text-green-600' : 'text-red-600'}`}>
                          {pct(est.porcentajeAsistencia)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </Tabla>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 4 – Morosidad
// ══════════════════════════════════════════════════════════════
const TabMorosidad: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,  setAnioId]  = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [data,    setData]    = useState<ReporteMorosidad | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.morosidad(Number(anioId))); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    exportarExcel(`Morosidad_${data.anio}`, [{
      nombre: 'Morosidad',
      filas: [
        [`Reporte de Morosidad · ${data.anio}`],
        [`Total deudores: ${data.totalDeudores}  |  Total pendiente: ${sol(data.totalPendiente)}`],
        [],
        ['Estudiante', 'DNI', 'Nivel', 'Grado', 'Sección', 'Matrícula pendiente', 'Monto matrícula', 'Pensiones vencidas', 'Monto pensiones', 'Total deuda'],
        ...data.deudores.map(d => [
          `${d.apellidos}, ${d.nombres}`, d.dni, d.gradoNivel, d.gradoNombre, d.seccionNombre,
          d.matriculaPendiente ? 'Sí' : 'No', d.montoMatriculaPendiente,
          d.pensionesVencidas, d.montoPensionesVencidas, d.totalDeuda,
        ]),
      ] as (string | number | null)[][],
    }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const rows = data.deudores.map(d =>
      `<tr><td>${d.apellidos}, ${d.nombres}<br/><span style="color:#94a3b8;font-size:9px;">${d.dni}</span></td>
       <td><span style="font-size:9px;font-weight:700;">${d.gradoNivel}</span><br/>${d.gradoNombre} – ${d.seccionNombre}</td>
       <td style="text-align:right;">${d.matriculaPendiente ? `<span style="color:#dc2626;font-weight:700;">${sol(d.montoMatriculaPendiente)}</span>` : '<span style="color:#94a3b8;">Pagada</span>'}</td>
       <td style="text-align:right;">${d.pensionesVencidas > 0 ? `<span style="color:#dc2626;font-weight:700;">${sol(d.montoPensionesVencidas)}</span><br/><span style="color:#94a3b8;font-size:9px;">${d.pensionesVencidas} cuota(s)</span>` : '<span style="color:#94a3b8;">Al día</span>'}</td>
       <td style="text-align:right;color:#dc2626;font-weight:700;">${sol(d.totalDeuda)}</td></tr>`
    ).join('');
    exportarPDF(
      `Morosidad · ${data.anio}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Total deudores</div><div class="kpi-val" style="color:#dc2626;">${data.totalDeudores}</div></div>
        <div class="kpi"><div class="kpi-lbl">Total pendiente</div><div class="kpi-val" style="color:#dc2626;">${sol(data.totalPendiente)}</div></div>
      </div>
      <table><thead><tr><th>Estudiante</th><th>Grado / Sección</th><th>Matrícula</th><th>Pensiones</th><th>Total deuda</th></tr></thead>
      <tbody>${rows}</tbody></table>`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="grid grid-cols-2 gap-4 flex-1">
              <KpiCard label="Total deudores" value={String(data.totalDeudores)} color="bg-red-100 text-red-700"/>
              <KpiCard label="Total pendiente" value={sol(data.totalPendiente)} color="bg-red-100 text-red-700"/>
            </div>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
          </div>

          {data.deudores.length === 0 ? <Empty msg="Sin deudores"/> : (
            <Tabla heads={['Estudiante', 'Nivel', 'Grado / Sección', 'Matrícula', 'Pensiones', 'Total deuda']}>
              {data.deudores.map(d => (
                <tr key={d.estudianteId} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-800">{d.apellidos}, {d.nombres}</p>
                    <p className="text-xs text-slate-400">{d.dni}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${NIVEL_COLOR[d.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                      {d.gradoNivel}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm">{d.gradoNombre} – {d.seccionNombre}</td>
                  <td className="px-4 py-3">
                    {d.matriculaPendiente
                      ? <span className="text-red-600 font-semibold">{sol(d.montoMatriculaPendiente)}</span>
                      : <span className="text-slate-300 text-xs">Pagada</span>}
                  </td>
                  <td className="px-4 py-3">
                    {d.pensionesVencidas > 0
                      ? <div>
                          <span className="text-red-600 font-semibold">{sol(d.montoPensionesVencidas)}</span>
                          <p className="text-xs text-slate-400">{d.pensionesVencidas} cuota{d.pensionesVencidas !== 1 ? 's' : ''}</p>
                        </div>
                      : <span className="text-slate-300 text-xs">Al día</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-lg font-bold text-red-700 tabular-nums">{sol(d.totalDeuda)}</span>
                  </td>
                </tr>
              ))}
            </Tabla>
          )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 5 – Ingresos
// ══════════════════════════════════════════════════════════════
const TabIngresos: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,  setAnioId]  = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [mes,     setMes]     = useState('');
  const [data,    setData]    = useState<ReporteIngresos | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.ingresos(Number(anioId), mes ? Number(mes) : undefined)); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const periodo = data.mesFiltro ? MESES[data.mesFiltro] : 'Año completo';
    exportarExcel(`Ingresos_${data.anio}${data.mesFiltro ? '_' + MESES[data.mesFiltro] : ''}`, [{
      nombre: 'Ingresos',
      filas: [
        [`Reporte de Ingresos · ${data.anio} — ${periodo}`],
        [`Total ingresos: ${sol(data.totalIngresos)}  |  Matrículas: ${sol(data.totalMatriculas)}  |  Pensiones: ${sol(data.totalPensiones)}`],
        [],
        ['Mes', 'Matrículas', 'Pensiones', 'Total', 'Operaciones'],
        ...data.resumenPorMes.map(m => [m.mesNombre, m.totalMatriculas, m.totalPensiones, m.total, m.cantidadOperaciones]),
      ] as (string | number | null)[][],
    }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const rows = data.resumenPorMes.map(m =>
      `<tr><td style="font-weight:700;">${m.mesNombre}</td>
       <td style="text-align:right;color:#2563eb;">${sol(m.totalMatriculas)}</td>
       <td style="text-align:right;color:#7c3aed;">${sol(m.totalPensiones)}</td>
       <td style="text-align:right;color:#16a34a;font-weight:700;">${sol(m.total)}</td>
       <td style="text-align:center;">${m.cantidadOperaciones}</td></tr>`
    ).join('');
    exportarPDF(
      `Ingresos · ${data.anio}${data.mesFiltro ? ' — ' + MESES[data.mesFiltro] : ''}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Total ingresos</div><div class="kpi-val" style="color:#16a34a;">${sol(data.totalIngresos)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Matrículas (${data.cantidadMatriculasPagadas})</div><div class="kpi-val" style="color:#2563eb;">${sol(data.totalMatriculas)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Pensiones (${data.cantidadPensionesPagadas})</div><div class="kpi-val" style="color:#7c3aed;">${sol(data.totalPensiones)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Período</div><div class="kpi-val" style="font-size:12px;">${data.mesFiltro ? MESES[data.mesFiltro] : 'Año completo'}</div></div>
      </div>
      <table><thead><tr><th>Mes</th><th>Matrículas</th><th>Pensiones</th><th>Total</th><th>Operaciones</th></tr></thead>
      <tbody>${rows}</tbody></table>`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <SelectField label="Mes (opcional)" value={mes} onChange={setMes}>
            <option value="">Todos los meses</option>
            {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </SelectField>
          <div/>
          <BtnGenerar onClick={generar} loading={loading} disabled={!anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
              <KpiCard label="Total ingresos"    value={sol(data.totalIngresos)}    color="bg-green-100 text-green-700"/>
              <KpiCard label="Matrículas"        value={sol(data.totalMatriculas)}  sub={`${data.cantidadMatriculasPagadas} pagos`} color="bg-blue-100 text-blue-700"/>
              <KpiCard label="Pensiones"         value={sol(data.totalPensiones)}   sub={`${data.cantidadPensionesPagadas} pagos`} color="bg-purple-100 text-purple-700"/>
              <KpiCard label="Período"           value={data.mesFiltro ? MESES[data.mesFiltro] : 'Año completo'}/>
            </div>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
          </div>

          <Tabla heads={['Mes', 'Matrículas', 'Pensiones', 'Total', 'Operaciones']}>
            {data.resumenPorMes.map(m => (
              <tr key={m.mes} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-800">{m.mesNombre}</td>
                <td className="px-4 py-3 tabular-nums text-blue-600">{sol(m.totalMatriculas)}</td>
                <td className="px-4 py-3 tabular-nums text-purple-600">{sol(m.totalPensiones)}</td>
                <td className="px-4 py-3 tabular-nums font-bold text-green-700">{sol(m.total)}</td>
                <td className="px-4 py-3 text-slate-500 tabular-nums">{m.cantidadOperaciones}</td>
              </tr>
            ))}
          </Tabla>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 6 – Descuentos Aplicados
// ══════════════════════════════════════════════════════════════
const TabDescuentos: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,  setAnioId]  = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [data,    setData]    = useState<ReporteDescuentos | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [abierto, setAbierto] = useState<number | null>(null);

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.descuentos(Number(anioId))); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const filas: (string | number | null)[][] = [
      [`Descuentos Aplicados · ${data.anio}`],
      [`Total estudiantes con descuento: ${data.totalEstudiantesConDescuento}`],
      [],
      ['Tipo descuento', 'Tipo', 'Valor', 'Aplica matrícula', 'Aplica pensión', 'Estudiante', 'DNI', 'Nivel', 'Grado', 'Sección'],
    ];
    data.tiposDescuento.forEach(td => {
      td.estudiantes.forEach(e => {
        filas.push([
          td.nombre,
          td.tipo,
          td.tipo === 'PORCENTAJE' ? `${td.valor}%` : sol(td.valor),
          td.aplicaMatricula ? 'Sí' : 'No',
          td.aplicaPension ? 'Sí' : 'No',
          `${e.apellidos}, ${e.nombres}`,
          e.dni, e.gradoNivel, e.gradoNombre, e.seccionNombre,
        ]);
      });
    });
    exportarExcel(`Descuentos_${data.anio}`, [{ nombre: 'Descuentos', filas }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const secciones = data.tiposDescuento.map(td => {
      const valorStr = td.tipo === 'PORCENTAJE' ? `${td.valor}%` : sol(td.valor);
      const rows = td.estudiantes.map(e =>
        `<tr><td>${e.apellidos}, ${e.nombres}</td><td>${e.dni}</td><td>${e.gradoNivel}</td><td>${e.gradoNombre}</td><td>${e.seccionNombre}</td></tr>`
      ).join('');
      return `<div class="section-hdr">${td.nombre} — ${valorStr} · ${td.cantidadEstudiantes} estudiante(s)</div>
        <table><thead><tr><th>Estudiante</th><th>DNI</th><th>Nivel</th><th>Grado</th><th>Sección</th></tr></thead>
        <tbody>${rows}</tbody></table>`;
    }).join('');
    exportarPDF(
      `Descuentos · ${data.anio}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Estudiantes con descuento</div><div class="kpi-val">${data.totalEstudiantesConDescuento}</div></div>
        <div class="kpi"><div class="kpi-lbl">Tipos activos</div><div class="kpi-val">${data.tiposDescuento.length}</div></div>
      </div>${secciones}`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="grid grid-cols-2 gap-4 flex-1">
              <KpiCard label="Estudiantes con descuento" value={String(data.totalEstudiantesConDescuento)} color="bg-teal-100 text-teal-700"/>
              <KpiCard label="Tipos de descuento activos" value={String(data.tiposDescuento.length)}/>
            </div>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
          </div>

          {data.tiposDescuento.map(td => (
            <div key={td.tipoDescuentoId} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <button className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                onClick={() => setAbierto(prev => prev === td.tipoDescuentoId ? null : td.tipoDescuentoId)}>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{td.nombre}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full
                      ${td.tipo === 'PORCENTAJE' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                      {td.tipo === 'PORCENTAJE' ? `${td.valor}%` : sol(td.valor)}
                    </span>
                    {td.aplicaMatricula && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Matrícula</span>}
                    {td.aplicaPension   && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">Pensión</span>}
                  </div>
                </div>
                <span className="text-sm font-bold text-slate-600">{td.cantidadEstudiantes} estudiante{td.cantidadEstudiantes !== 1 ? 's' : ''}</span>
                <svg className={`w-4 h-4 text-slate-400 transition-transform ${abierto === td.tipoDescuentoId ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
                </svg>
              </button>

              {abierto === td.tipoDescuentoId && (
                <div className="border-t border-slate-100">
                  <Tabla heads={['Estudiante', 'DNI', 'Nivel', 'Grado', 'Sección']}>
                    {td.estudiantes.map(e => (
                      <tr key={e.estudianteId} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-800">{e.apellidos}, {e.nombres}</td>
                        <td className="px-4 py-3 text-slate-500 tabular-nums">{e.dni}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${NIVEL_COLOR[e.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                            {e.gradoNivel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{e.gradoNombre}</td>
                        <td className="px-4 py-3 text-slate-600">{e.seccionNombre}</td>
                      </tr>
                    ))}
                  </Tabla>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 7 – Resumen de Matrículas
// ══════════════════════════════════════════════════════════════
const TabMatriculas: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,  setAnioId]  = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [data,    setData]    = useState<ReporteMatriculas | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.resumenMatriculas(Number(anioId))); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const filas: (string | number | null)[][] = [
      [`Resumen de Matrículas · ${data.anio}`],
      [`Total matriculados: ${data.totalMatriculados}`],
      [],
      ['Nivel', 'Total nivel', 'Grado', 'Total grado', 'Sección', 'Total sección'],
    ];
    data.niveles.forEach(n => {
      n.grados.forEach(g => {
        g.secciones.forEach(s => {
          filas.push([n.nivel, n.total, g.gradoNombre, g.total, `Sección ${s.seccionNombre}`, s.total]);
        });
      });
    });
    exportarExcel(`Matriculas_${data.anio}`, [{ nombre: 'Matrículas', filas }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const seccionesHtml = data.niveles.map(n => {
      const gradosRows = n.grados.map(g => {
        const secRows = g.secciones.map(s =>
          `<tr><td>${n.nivel}</td><td>${g.gradoNombre}</td><td>Sección ${s.seccionNombre}</td><td style="text-align:center;font-weight:700;">${s.total}</td></tr>`
        ).join('');
        return secRows;
      }).join('');
      return `<div class="section-hdr">${n.nivel} — ${n.total} matriculados</div>
        <table><thead><tr><th>Nivel</th><th>Grado</th><th>Sección</th><th>Total</th></tr></thead>
        <tbody>${gradosRows}</tbody></table>`;
    }).join('');
    exportarPDF(
      `Resumen de Matrículas · ${data.anio}`,
      `<div class="kpi-row"><div class="kpi"><div class="kpi-lbl">Total matriculados</div><div class="kpi-val">${data.totalMatriculados}</div></div></div>
      ${seccionesHtml}`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <KpiCard label="Total matriculados" value={String(data.totalMatriculados)} color="bg-blue-100 text-blue-700"/>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
          </div>

          {data.niveles.map(nivel => (
            <div key={nivel.nivel} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              {/* Nivel header */}
              <div className={`px-5 py-3 flex items-center justify-between
                ${nivel.nivel === 'INICIAL' ? 'bg-amber-50 border-b border-amber-100'
                : nivel.nivel === 'PRIMARIA' ? 'bg-blue-50 border-b border-blue-100'
                : 'bg-purple-50 border-b border-purple-100'}`}>
                <span className={`font-bold text-sm px-3 py-1 rounded-full ${NIVEL_COLOR[nivel.nivel] ?? 'bg-slate-100 text-slate-700'}`}>
                  {nivel.nivel}
                </span>
                <span className="font-bold text-slate-700">{nivel.total} matriculados</span>
              </div>

              {/* Grados */}
              {nivel.grados.map(grado => (
                <div key={grado.gradoId} className="border-b border-slate-50 last:border-0">
                  <div className="flex items-center justify-between px-5 py-2.5 bg-slate-50/60">
                    <span className="text-sm font-semibold text-slate-700">{grado.gradoNombre}</span>
                    <span className="text-xs font-bold text-slate-500">{grado.total} total</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-3">
                    {grado.secciones.map(sec => (
                      <div key={sec.seccionId} className="flex items-center justify-between
                                                          bg-white rounded-xl border border-slate-100 px-3 py-2.5">
                        <span className="text-sm text-slate-600 font-semibold">Sección {sec.seccionNombre}</span>
                        <span className="text-lg font-bold text-slate-800 tabular-nums">{sec.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB 8 – Carga Docente
// ══════════════════════════════════════════════════════════════
const TabCargaDocente: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId,  setAnioId]  = useState(() => años[0] ? String(años[0].idAnioEscolar) : '');
  const [data,    setData]    = useState<ReporteCargaDocente | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [abierto, setAbierto] = useState<number | null>(null);

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try { setData(await reporteService.cargaDocente(Number(anioId))); }
    catch (e: any) { setError(e.message ?? 'Error'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    const filas: (string | number | null)[][] = [
      [`Carga Docente · ${data.anio}`],
      [`Total docentes: ${data.totalDocentes}`],
      [],
      ['Docente', 'DNI', 'Total asignaciones', 'Curso', 'Grado', 'Nivel', 'Sección'],
    ];
    data.docentes.forEach(doc => {
      doc.asignaciones.forEach(a => {
        filas.push([
          `${doc.apellidos}, ${doc.nombres}`, doc.dni, doc.totalAsignaciones,
          a.cursoNombre, a.gradoNombre, a.gradoNivel, a.seccionNombre,
        ]);
      });
    });
    exportarExcel(`CargaDocente_${data.anio}`, [{ nombre: 'Carga Docente', filas }]);
  };

  const handlePDF = () => {
    if (!data) return;
    const rows = data.docentes.flatMap(doc =>
      doc.asignaciones.map((a, i) =>
        `<tr>
          ${i === 0 ? `<td rowspan="${doc.asignaciones.length}" style="font-weight:700;vertical-align:top;">${doc.apellidos}, ${doc.nombres}<br/><span style="color:#94a3b8;font-size:9px;">${doc.dni}</span></td>` : ''}
          <td>${a.cursoNombre}</td><td>${a.gradoNombre}</td><td>${a.gradoNivel}</td><td>Sección ${a.seccionNombre}</td>
        </tr>`
      )
    ).join('');
    exportarPDF(
      `Carga Docente · ${data.anio}`,
      `<div class="kpi-row"><div class="kpi"><div class="kpi-lbl">Total docentes</div><div class="kpi-val">${data.totalDocentes}</div></div></div>
      <table><thead><tr><th>Docente</th><th>Curso</th><th>Grado</th><th>Nivel</th><th>Sección</th></tr></thead>
      <tbody>${rows}</tbody></table>`,
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex gap-4 items-end">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <BtnGenerar onClick={generar} loading={loading} disabled={!anioId}/>
        </div>
      </div>

      {error && <ErrBox msg={error}/>}
      {loading && <Spin/>}
      {data && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <KpiCard label="Total docentes" value={String(data.totalDocentes)} color="bg-indigo-100 text-indigo-700"/>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF}/>
          </div>

          {data.docentes.map(doc => (
            <div key={doc.docenteId} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <button className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                onClick={() => setAbierto(prev => prev === doc.docenteId ? null : doc.docenteId)}>
                <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 font-bold text-sm
                                flex items-center justify-center flex-shrink-0">
                  {doc.nombres.charAt(0)}{doc.apellidos.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{doc.apellidos}, {doc.nombres}</p>
                  <p className="text-xs text-slate-400">DNI: {doc.dni}</p>
                </div>
                <span className="text-sm font-bold text-indigo-600">
                  {doc.totalAsignaciones} asignacion{doc.totalAsignaciones !== 1 ? 'es' : ''}
                </span>
                <svg className={`w-4 h-4 text-slate-400 transition-transform ${abierto === doc.docenteId ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
                </svg>
              </button>

              {abierto === doc.docenteId && (
                <div className="border-t border-slate-100">
                  <Tabla heads={['Curso', 'Grado', 'Nivel', 'Sección']}>
                    {doc.asignaciones.map(a => (
                      <tr key={a.asignacionId} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-semibold text-slate-800">{a.cursoNombre}</td>
                        <td className="px-4 py-3 text-slate-600">{a.gradoNombre}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${NIVEL_COLOR[a.gradoNivel] ?? 'bg-slate-100 text-slate-600'}`}>
                            {a.gradoNivel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">Sección {a.seccionNombre}</td>
                      </tr>
                    ))}
                  </Tabla>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// TAB ROD — Registro de Oferta Docente
// ══════════════════════════════════════════════════════════════
const TabRod: React.FC<{ años: AñoEscolar[] }> = ({ años }) => {
  const [anioId, setAnioId]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { if (años.length) setAnioId(String(años[0].idAnioEscolar)); }, [años]);

  const handleDescargar = async () => {
    if (!anioId) return;
    setLoading(true); setError('');
    try {
      const data = await documentoService.getRod(Number(anioId));
      exportarPDF('Registro de Oferta Docente (ROD)', htmlRod(data));
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el ROD');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <p className="text-sm text-slate-500 mb-4">
          El ROD (Registro de Oferta Docente) detalla todas las asignaciones docente-curso del año escolar seleccionado.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <SelectField label="Año escolar" value={anioId} onChange={setAnioId}>
            <option value="">— Seleccionar —</option>
            {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
          </SelectField>
          <button
            onClick={handleDescargar}
            disabled={loading || !anioId}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700
                       text-white text-sm font-bold transition-colors shadow-sm
                       disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading
              ? <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                </svg>
              : <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
                </svg>}
            Descargar ROD (PDF)
          </button>
        </div>
        {error && <ErrBox msg={error} />}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
// TAB 10 – Cobros Adicionales
// ══════════════════════════════════════════════════════════════
type ModoCobros = 'dia' | 'mes' | 'anio' | 'intervalo';
const MODOS_COBROS: { key: ModoCobros; label: string }[] = [
  { key: 'dia',       label: 'Por día' },
  { key: 'mes',       label: 'Por mes' },
  { key: 'anio',      label: 'Por año' },
  { key: 'intervalo', label: 'Intervalo' },
];
const ESTADO_COBRO_STYLE: Record<string, string> = {
  PENDIENTE: 'bg-amber-100 text-amber-700',
  PAGADO:    'bg-green-100 text-green-700',
  ANULADO:   'bg-slate-100 text-slate-400',
};

const TabReporteCobros: React.FC = () => {
  const anioActual = new Date().getFullYear();
  const [modo,      setModo]      = useState<ModoCobros>('mes');
  const [fecha,     setFecha]     = useState(() => new Date().toISOString().slice(0, 10));
  const [mes,       setMes]       = useState(() => String(new Date().getMonth() + 1));
  const [anio,      setAnio]      = useState(() => String(anioActual));
  const [desde,     setDesde]     = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10);
  });
  const [hasta,     setHasta]     = useState(() => new Date().toISOString().slice(0, 10));
  const [data,      setData]      = useState<ReporteCobros | null>(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const generar = async () => {
    setLoading(true); setError(''); setData(null);
    try {
      let result: ReporteCobros;
      switch (modo) {
        case 'dia':       result = await reporteCobrosService.porDia(fecha); break;
        case 'mes':       result = await reporteCobrosService.porMes(Number(mes), Number(anio)); break;
        case 'anio':      result = await reporteCobrosService.porAnio(Number(anio)); break;
        case 'intervalo': result = await reporteCobrosService.porIntervalo(desde, hasta); break;
      }
      setData(result);
    } catch (e: any) { setError(e.message ?? 'Error al generar reporte'); }
    finally { setLoading(false); }
  };

  const handleExcel = () => {
    if (!data) return;
    exportarExcel(`Cobros_${data.periodoDescripcion.replace(/\s/g, '_')}`, [
      {
        nombre: 'Resumen',
        filas: [
          [`Reporte de Cobros Adicionales — ${data.periodoDescripcion}`],
          [`Desde: ${data.fechaDesde}   Hasta: ${data.fechaHasta}`],
          [],
          ['Total cobros', data.totalCobros],
          ['Monto total',  data.montoTotal],
          ['Monto pagado', data.montoPagado],
          ['Pendiente',    data.montoPendiente],
          ['Anulado',      data.montoAnulado],
          [],
          ['Categoría', 'Cantidad', 'Monto total', 'Pagado', 'Pendiente'],
          ...data.porCategoria.map(c => [c.categoria, c.cantidad, c.monto, c.montoPagado, c.montoPendiente]),
        ] as (string | number | null)[][],
      },
      {
        nombre: 'Detalle',
        filas: [
          ['#', 'Fecha', 'Persona', 'DNI', 'Concepto', 'Categoría', 'Monto', 'Estado', 'Fecha Pago', 'Comprobante', 'Registrado por'],
          ...data.detalle.map((d, i) => [
            i + 1, d.fecha, d.estudiante, d.dni, d.concepto, d.categoria,
            d.monto, d.estado, d.fechaPago, d.comprobanteNumero, d.registradoPor,
          ]),
        ] as (string | number | null)[][],
      },
    ]);
  };

  const handlePDF = () => {
    if (!data) return;
    const filasCateg = data.porCategoria.map(c =>
      `<tr>
        <td style="font-weight:600;">${c.categoria}</td>
        <td style="text-align:center;">${c.cantidad}</td>
        <td style="text-align:right;">${sol(c.monto)}</td>
        <td style="text-align:right;color:#16a34a;">${sol(c.montoPagado)}</td>
        <td style="text-align:right;color:#d97706;">${sol(c.montoPendiente)}</td>
      </tr>`
    ).join('');
    const filasDetalle = data.detalle.map(d => {
      const color = d.estado === 'PAGADO' ? '#16a34a' : d.estado === 'ANULADO' ? '#94a3b8' : '#d97706';
      return `<tr>
        <td>${d.fecha}</td>
        <td>${d.estudiante}</td>
        <td style="font-family:monospace;">${d.dni ?? '—'}</td>
        <td>${d.concepto}</td>
        <td>${d.categoria}</td>
        <td style="text-align:right;font-weight:600;">${sol(d.monto)}</td>
        <td style="text-align:center;color:${color};font-weight:700;">${d.estado}</td>
        <td>${d.fechaPago ?? '—'}</td>
      </tr>`;
    }).join('');

    exportarPDF(
      `Cobros Adicionales — ${data.periodoDescripcion}`,
      `<div class="kpi-row">
        <div class="kpi"><div class="kpi-lbl">Total cobros</div><div class="kpi-val">${data.totalCobros}</div></div>
        <div class="kpi"><div class="kpi-lbl">Monto total</div><div class="kpi-val">${sol(data.montoTotal)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Pagado</div><div class="kpi-val" style="color:#16a34a;">${sol(data.montoPagado)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Pendiente</div><div class="kpi-val" style="color:#d97706;">${sol(data.montoPendiente)}</div></div>
        <div class="kpi"><div class="kpi-lbl">Anulado</div><div class="kpi-val" style="color:#94a3b8;">${sol(data.montoAnulado)}</div></div>
      </div>
      <div class="section-hdr">Por categoría</div>
      <table>
        <thead><tr><th>Categoría</th><th>Cant.</th><th>Total</th><th>Pagado</th><th>Pendiente</th></tr></thead>
        <tbody>${filasCateg || '<tr><td colspan="5" style="text-align:center;color:#94a3b8;">Sin categorías</td></tr>'}</tbody>
      </table>
      <div class="section-hdr">Detalle de cobros</div>
      <table>
        <thead><tr><th>Fecha</th><th>Persona</th><th>DNI</th><th>Concepto</th><th>Categoría</th><th>Monto</th><th>Estado</th><th>Fecha pago</th></tr></thead>
        <tbody>${filasDetalle || '<tr><td colspan="8" style="text-align:center;color:#94a3b8;">Sin registros</td></tr>'}</tbody>
      </table>`,
    );
  };

  const años = Array.from({ length: 6 }, (_, i) => anioActual - i);

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
        {/* Selector de modo */}
        <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
          {MODOS_COBROS.map(m => (
            <button key={m.key} onClick={() => { setModo(m.key); setData(null); setError(''); }}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-colors
                ${modo === m.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Inputs según modo */}
        <div className="flex flex-wrap gap-4 items-end">
          {modo === 'dia' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha</label>
              <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          )}
          {modo === 'mes' && (
            <>
              <SelectField label="Mes" value={mes} onChange={setMes}>
                {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </SelectField>
              <SelectField label="Año" value={anio} onChange={setAnio}>
                {años.map(y => <option key={y} value={y}>{y}</option>)}
              </SelectField>
            </>
          )}
          {modo === 'anio' && (
            <SelectField label="Año" value={anio} onChange={setAnio}>
              {años.map(y => <option key={y} value={y}>{y}</option>)}
            </SelectField>
          )}
          {modo === 'intervalo' && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Desde</label>
                <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Hasta</label>
                <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
                  className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <p className="text-xs text-slate-400 self-end pb-2">Máx. 61 días</p>
            </>
          )}
          <BtnGenerar onClick={generar} loading={loading} />
        </div>
      </div>

      {error && <ErrBox msg={error} />}
      {loading && <Spin />}

      {data && (
        <div className="space-y-5">
          {/* KPIs + export */}
          <div className="flex items-start gap-4 flex-wrap">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 flex-1">
              <KpiCard label="Total cobros"  value={String(data.totalCobros)} />
              <KpiCard label="Monto total"   value={sol(data.montoTotal)}   color="bg-slate-100 text-slate-700" />
              <KpiCard label="Pagado"        value={sol(data.montoPagado)}   color="bg-green-100 text-green-700" />
              <KpiCard label="Pendiente"     value={sol(data.montoPendiente)} color="bg-amber-100 text-amber-700" />
              <KpiCard label="Anulado"       value={sol(data.montoAnulado)}  color="bg-slate-100 text-slate-500" />
            </div>
            <BtnDescarga onExcel={handleExcel} onPDF={handlePDF} />
          </div>

          {/* Badges resumen */}
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Pagados',   d: data.pagados,   cls: 'bg-green-50 border-green-200 text-green-800' },
              { label: 'Pendientes',d: data.pendientes,cls: 'bg-amber-50 border-amber-200 text-amber-800' },
              { label: 'Anulados',  d: data.anulados,  cls: 'bg-slate-50 border-slate-200 text-slate-600' },
            ].map(({ label, d, cls }) => (
              <div key={label} className={`border rounded-xl px-4 py-2.5 flex items-center gap-3 ${cls}`}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</p>
                  <p className="font-bold tabular-nums">{sol(d.monto)}</p>
                </div>
                <span className="text-2xl font-extrabold tabular-nums opacity-60">{d.cantidad}</span>
              </div>
            ))}
          </div>

          {/* Por categoría */}
          {data.porCategoria.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-600 mb-2">Por categoría</h3>
              <Tabla heads={['Categoría', 'Cantidad', 'Monto total', 'Pagado', 'Pendiente', '% pagado']}>
                {data.porCategoria.map(c => {
                  const pctPag = c.monto > 0 ? Math.round((c.montoPagado / c.monto) * 100) : 0;
                  return (
                    <tr key={c.categoria} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                          {c.categoria}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-slate-700">{c.cantidad}</td>
                      <td className="px-4 py-3 font-bold tabular-nums text-slate-800">{sol(c.monto)}</td>
                      <td className="px-4 py-3 tabular-nums text-green-700 font-semibold">{sol(c.montoPagado)}</td>
                      <td className="px-4 py-3 tabular-nums text-amber-700">{sol(c.montoPendiente)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-green-500 rounded-full" style={{ width: `${pctPag}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 tabular-nums">{pctPag}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Tabla>
            </div>
          )}

          {/* Detalle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-600">
                Detalle — {data.periodoDescripcion}
              </h3>
              <span className="text-xs text-slate-400">{data.detalle.length} registro{data.detalle.length !== 1 ? 's' : ''}</span>
            </div>
            {data.detalle.length === 0 ? (
              <Empty msg="Sin cobros en el período seleccionado" />
            ) : (
              <Tabla heads={['Fecha', 'Persona', 'Concepto', 'Categoría', 'Monto', 'Estado', 'Fecha pago', 'Registrado por']}>
                {data.detalle.map(d => (
                  <tr key={d.idCobro} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{d.fecha}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800 text-sm leading-tight">{d.estudiante}</p>
                      {d.dni && <p className="text-xs text-slate-400 font-mono">{d.dni}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-sm">{d.concepto}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                        {d.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold tabular-nums text-slate-800 whitespace-nowrap">
                      {sol(d.monto)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap
                        ${ESTADO_COBRO_STYLE[d.estado] ?? 'bg-slate-100 text-slate-500'}`}>
                        {d.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {d.fechaPago ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[140px] truncate">
                      {d.registradoPor}
                    </td>
                  </tr>
                ))}
              </Tabla>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
const ReportesPage: React.FC = () => {
  const { user }  = useAuth();
  const rol       = user?.rol ?? '';
  const tabsVis   = TABS.filter(t => t.roles.includes(rol));
  const [tab, setTab] = useState<TabKey>(() => tabsVis[0]?.key ?? 'notas');

  const [años,     setAños]     = useState<AñoEscolar[]>([]);
  const [grados,   setGrados]   = useState<Grado[]>([]);
  const [secciones,setSecciones]= useState<Seccion[]>([]);

  const cargarCatalogos = useCallback(async () => {
    try {
      const [a, g, s] = await Promise.all([
        catalogoService.getAñosEscolares(),
        catalogoService.getGrados(),
        catalogoService.getSecciones(),
      ]);
      setAños(a); setGrados(g); setSecciones(s);
    } catch {}
  }, []);

  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);

  if (tabsVis.length === 0) return <Empty msg="No tienes acceso a reportes"/>;

  // Iconos visuales por tipo de reporte
  const tabIcons: Record<TabKey, keyof typeof I> = {
    'notas':      'Award',
    'asist-est':  'CheckCircle',
    'asist-sec':  'Calendar',
    'morosidad':  'AlertCircle',
    'ingresos':   'DollarSign',
    'descuentos': 'Wallet',
    'matriculas': 'Users',
    'carga':      'Briefcase',
    'rod':        'FileText',
    'cobros':     'DollarSign',
  };
  const tabColors: Record<TabKey, { bg: string; fg: string }> = {
    'notas':      { bg: 'var(--primary-100)', fg: 'var(--primary-700)' },
    'asist-est':  { bg: '#dcfce7', fg: '#166534' },
    'asist-sec':  { bg: 'var(--accent-100)', fg: 'var(--accent-700)' },
    'morosidad':  { bg: '#fee2e2', fg: '#991b1b' },
    'ingresos':   { bg: '#fef3c7', fg: '#92400e' },
    'descuentos': { bg: 'var(--accent-100)', fg: 'var(--accent-700)' },
    'matriculas': { bg: '#dbeafe', fg: '#1e40af' },
    'carga':      { bg: 'var(--primary-100)', fg: 'var(--primary-700)' },
    'rod':        { bg: '#dbeafe', fg: '#1e40af' },
    'cobros':     { bg: '#ede9fe', fg: '#5b21b6' },
  };

  const tabDescr: Record<TabKey, string> = {
    'notas':      'Promedios por sección y curso',
    'asist-est':  'Historial individual de asistencia',
    'asist-sec':  'Asistencia consolidada por sección',
    'morosidad':  'Deudas vencidas y pendientes',
    'ingresos':   'Cobros y recaudación mensual',
    'descuentos': 'Descuentos aplicados',
    'matriculas': 'Evolución de matrícula',
    'carga':      'Carga horaria docente',
    'rod':        'Relación oficial de docentes',
    'cobros':     'Recaudación de cobros adicionales por período',
  };

  const tabActual = tabsVis.find(t => t.key === tab);
  const colActual = tabColors[tab];
  const ActualIcon = I[tabIcons[tab]];

  return (
    <>
      <PageHead
        title="Reportes"
        sub="Selecciona un reporte y configura los parámetros"
      />

      <div
        className="grid gap-4"
        data-reportes-grid
        style={{ gridTemplateColumns: '320px 1fr', alignItems: 'flex-start' }}
      >
        {/* Catálogo de reportes */}
        <Card pad={false} className="reportes-catalogo">
          <div
            className="overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 240px)' }}
          >
            {tabsVis.map(t => {
              const c = tabColors[t.key];
              const Icon = I[tabIcons[t.key]];
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: 14,
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    borderBottom: '1px solid var(--border)',
                    background: active ? 'var(--primary-50)' : 'white',
                    borderLeft: active ? '3px solid var(--primary-600)' : '3px solid transparent',
                    transition: 'all .15s',
                  }}
                >
                  <div
                    style={{
                      width: 36, height: 36,
                      borderRadius: 9,
                      background: c.bg,
                      color: c.fg,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-ink-900">{t.label}</div>
                    <div className="text-[11px] text-ink-500 mt-0.5 leading-relaxed">
                      {tabDescr[t.key]}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Configurador */}
        <Card pad={false}>
          <div
            className="px-6 py-5 flex gap-4 items-start border-b"
            style={{ borderColor: 'var(--border)' }}
          >
            <div
              className="rounded-r-lg grid place-items-center flex-shrink-0"
              style={{
                width: 56, height: 56,
                background: colActual.bg,
                color: colActual.fg,
              }}
            >
              <ActualIcon size={28} />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-lg font-extrabold text-ink-900 m-0"
                style={{ letterSpacing: '-.01em' }}
              >
                {tabActual?.label}
              </h2>
              <p className="text-[13px] text-ink-500 mt-1 m-0">{tabDescr[tab]}</p>
            </div>
          </div>

          <div className="p-6">
            {tab === 'notas'      && <TabNotasSeccion         años={años} grados={grados} secciones={secciones}/>}
            {tab === 'asist-est'  && <TabAsistenciaEstudiante años={años}/>}
            {tab === 'asist-sec'  && <TabAsistenciaSeccion    años={años} grados={grados} secciones={secciones}/>}
            {tab === 'morosidad'  && <TabMorosidad            años={años}/>}
            {tab === 'ingresos'   && <TabIngresos             años={años}/>}
            {tab === 'descuentos' && <TabDescuentos           años={años}/>}
            {tab === 'matriculas' && <TabMatriculas           años={años}/>}
            {tab === 'carga'      && <TabCargaDocente         años={años}/>}
            {tab === 'rod'        && <TabRod                  años={años}/>}
            {tab === 'cobros'     && <TabReporteCobros />}
          </div>
        </Card>
      </div>

      <style>{`
        @media (max-width: 1100px) {
          .reportes-catalogo {
            min-width: 0;
          }
          [data-reportes-grid] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default ReportesPage;
